import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  CssBaseline,
  Box,
  IconButton,
  Button,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Badge,
} from "@mui/material";
import {
  Logout as LogoutIcon,
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  Menu as MenuIcon,
  AccountCircle as AccountCircleIcon,
  Cloud as CloudIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
} from "@mui/icons-material";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import VersionInfo from "./VersionInfo";
import { db } from "../firebase";
import { collection, onSnapshot, query, where } from "firebase/firestore";

const Layout = ({ children }) => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const [unreadCount, setUnreadCount] = useState(0);

  // Listen unread notifications for current user (if any)
  useEffect(() => {
    if (!currentUser) {
      setUnreadCount(0);
      return;
    }
    try {
      const colRef = collection(
        db,
        "users",
        currentUser.uid,
        "notification_history"
      );
      const q = query(colRef, where("isRead", "==", false));
      const unsub = onSnapshot(
        q,
        (snap) => setUnreadCount(snap.size || 0),
        () => setUnreadCount(0)
      );
      return unsub;
    } catch (_) {
      setUnreadCount(0);
    }
  }, [currentUser]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  const menuItems = [
    { text: "Dashboard", path: "/" },
    { text: "Harita Yönetimi", path: "/map-management" },
    { text: "Pin Yönetimi", path: "/pin-management" },
    { text: "Kullanıcı Yönetimi", path: "/user-management" },
    { text: "Şikayet Listesi", path: "/complaint-list" },
    { text: "İçerik Denetimi", path: "/moderation-queue" },
    { text: "Kampanyalar", path: "/campaigns" },
    { text: "Toplu Pin Oluştur", path: "/bulk-pin-creation" },
    { text: "CSV Toplu Pin", path: "/csv-bulk-pin-creation" },
    { text: "Ses Kütüphanesi", path: "/audio-library" },
    { text: "Bildirimler", path: "/notifications" },
    { text: "Bildirim Logları", path: "/notification-logs" },
    { text: "Admin Logları", path: "/admin-logs" },
    { text: "Planlı Duyurular", path: "/announcements" },
    { text: "Şablonlar", path: "/templates" },
    { text: "Zaman Yönetimi", path: "/time-management" },
  ];

  return (
    <Box className="page" sx={{ minHeight: "100vh", bgcolor: "#f7f9fa" }}>
      <CssBaseline />

      {/* Cloudflare Benzeri Header */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: "#ffffff",
          color: "#1a202c",
          borderBottom: "1px solid #e2e8f0",
          height: "64px",
          width: "100vw",
          left: "50%",
          right: "auto",
          mx: "calc(50% - 50vw)",
        }}
      >
        <Toolbar sx={{ height: "64px", px: 3 }}>
          {/* Logo */}
          <Box sx={{ display: "flex", alignItems: "center", mr: 4 }}>
            <CloudIcon sx={{ color: "#f38020", fontSize: 28, mr: 1 }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: "1.125rem",
                color: "#1a202c",
                letterSpacing: "-0.025em",
              }}
            >
              PutPin Admin
            </Typography>
          </Box>

          {/* Hamburger Menu Button - Mobile */}
          {isMobile && (
            <IconButton
              color="inherit"
              onClick={() => setMobileMenuOpen(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Navigation Menu - Desktop */}
          <Box
            sx={{
              display: isMobile ? "none" : "flex",
              gap: 0,
              flexGrow: 1,
              overflowX: "auto",
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none",
            }}
          >
            {menuItems.map((item) => (
              <Button
                key={item.text}
                component={NavLink}
                to={item.path}
                color="inherit"
                sx={{
                  textTransform: "none",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  px: 1.5,
                  py: 1.5,
                  color: "#4a5568",
                  borderRadius: "6px",
                  mx: 0.25,
                  minWidth: "auto",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  "&.active": {
                    backgroundColor: "#f7f9fa",
                    color: "#f38020",
                    fontWeight: 600,
                  },
                  "&:hover": {
                    backgroundColor: "#f7f9fa",
                    color: "#1a202c",
                  },
                }}
              >
                {item.text}
              </Button>
            ))}
          </Box>

          {/* Right Side Actions - Cloudflare Style */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Search */}
            <IconButton
              sx={{
                color: "#4a5568",
                "&:hover": { backgroundColor: "#f7f9fa" },
              }}
            >
              <SearchIcon fontSize="small" />
            </IconButton>

            {/* Notifications */}
            <IconButton
              sx={{
                color: "#4a5568",
                "&:hover": { backgroundColor: "#f7f9fa" },
              }}
              onClick={() => navigate("/notifications")}
            >
              <Badge
                badgeContent={unreadCount}
                color="error"
                invisible={!unreadCount}
              >
                <NotificationsIcon fontSize="small" />
              </Badge>
            </IconButton>

            {/* User Menu */}
            {currentUser ? (
              <div>
                <IconButton
                  onClick={handleMenu}
                  sx={{
                    color: "#4a5568",
                    "&:hover": { backgroundColor: "#f7f9fa" },
                  }}
                >
                  <Avatar
                    alt={currentUser.displayName || "User"}
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: currentUser?.photoURL
                        ? "transparent"
                        : "primary.main",
                      fontSize: "0.875rem",
                    }}
                    src={currentUser?.photoURL}
                    onError={(e) => {
                      console.log(
                        "Avatar image failed to load:",
                        currentUser?.photoURL
                      );
                      e.target.style.display = "none";
                    }}
                  >
                    {!currentUser?.photoURL &&
                      (currentUser?.displayName?.[0] ||
                        currentUser?.email?.[0] ||
                        "U")}
                  </Avatar>
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      minWidth: 200,
                      boxShadow:
                        "0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                    },
                  }}
                >
                  <MenuItem onClick={handleClose} sx={{ py: 1.5 }}>
                    <ListItemIcon>
                      <AccountCircleIcon
                        fontSize="small"
                        sx={{ color: "#4a5568" }}
                      />
                    </ListItemIcon>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {currentUser.displayName || currentUser.email}
                    </Typography>
                  </MenuItem>

                  <Divider sx={{ my: 1 }} />

                  <MenuItem
                    onClick={() => {
                      window.open(
                        "https://console.firebase.google.com/u/0/project/echomapx/overview",
                        "_blank"
                      );
                      handleClose();
                    }}
                    sx={{ py: 1.5 }}
                  >
                    <ListItemIcon>
                      <Box component="span" sx={{ fontSize: 16 }}>
                        🔥
                      </Box>
                    </ListItemIcon>
                    <Typography variant="body2">Firebase Console</Typography>
                  </MenuItem>

                  <MenuItem
                    onClick={() => {
                      window.open(
                        "https://console.cloud.google.com/welcome?authuser=0&hl=en-US&inv=1&invt=Ab5X8w&project=echomapx",
                        "_blank"
                      );
                      handleClose();
                    }}
                    sx={{ py: 1.5 }}
                  >
                    <ListItemIcon>
                      <Box component="span" sx={{ fontSize: 16 }}>
                        ☁️
                      </Box>
                    </ListItemIcon>
                    <Typography variant="body2">
                      Google Cloud Console
                    </Typography>
                  </MenuItem>

                  <Divider sx={{ my: 1 }} />

                  <MenuItem onClick={handleClose} sx={{ py: 1.5 }}>
                    <ListItemIcon>
                      <SettingsIcon
                        fontSize="small"
                        sx={{ color: "#4a5568" }}
                      />
                    </ListItemIcon>
                    <Typography variant="body2">Ayarlar</Typography>
                  </MenuItem>

                  <MenuItem onClick={handleClose} sx={{ py: 1.5 }}>
                    <ListItemIcon>
                      <HelpIcon fontSize="small" sx={{ color: "#4a5568" }} />
                    </ListItemIcon>
                    <Typography variant="body2">Yardım</Typography>
                  </MenuItem>

                  <Divider sx={{ my: 1 }} />

                  <MenuItem onClick={handleLogout} sx={{ py: 1.5 }}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" sx={{ color: "#4a5568" }} />
                    </ListItemIcon>
                    <Typography variant="body2">Çıkış Yap</Typography>
                  </MenuItem>
                </Menu>
              </div>
            ) : (
              <Button
                variant="contained"
                onClick={() => navigate("/login")}
                sx={{
                  bgcolor: "#f38020",
                  color: "#ffffff",
                  fontWeight: 500,
                  "&:hover": { bgcolor: "#e06a00" },
                }}
              >
                Giriş Yap
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer Menu */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: 280,
            boxSizing: "border-box",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#1a202c" }}>
            PutPin Admin
          </Typography>
        </Box>
        <Divider />
        <List>
          {menuItems.map((item) => (
            <ListItem
              key={item.text}
              component={NavLink}
              to={item.path}
              onClick={() => setMobileMenuOpen(false)}
              sx={{
                textDecoration: "none",
                color: "inherit",
                "&.active": {
                  backgroundColor: "#f7f9fa",
                  "& .MuiListItemText-primary": {
                    color: "#f38020",
                    fontWeight: 600,
                  },
                },
                "&:hover": {
                  backgroundColor: "#f7f9fa",
                },
              }}
            >
              <ListItemText
                primary={item.text}
                sx={{
                  "& .MuiListItemText-primary": {
                    fontSize: "0.875rem",
                    fontWeight: 500,
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
      </Drawer>

      {/* Main Content */}
      <Box component="main" sx={{ p: 3 }}>
        {children}
      </Box>
      <VersionInfo />
    </Box>
  );
};

export default Layout;
